import { defaultCasurHydricVisibility, defaultHybridReferenceVisibility, type AppState } from './types';
import { DEFAULT_MASTER_STYLE, defaultClassificationConfig, defaultLabelConfig, defaultLegendConfig } from '../presentation/config';
import { defaultPresentationWorkspaceConfig } from '../presentation/workspace';
import type { ProjectMetadata } from './types';
import { defaultWorkspaceLayout, normalizeWorkspaceLayout } from '../ui/workspaceLayout';
import { emptyGeometryEditHistory } from '../domain/geometryEditing';
import { keepViewChoices, UNDOABLE_KEYS, type EditHistory, type HistoryEntry, type PendingStep } from './history';

type Listener = (state: AppState, previous: AppState) => void;

export interface UpdateOptions {
  readonly markDirty?: boolean;
  /** 'reset' starts a new undo history after this update (a Maestro is loaded); 'skip' keeps it out of the history. */
  readonly history?: 'skip' | 'reset';
}

/** Completes a history patch before it is applied (e.g. the active model derived from restored geometry edits). */
export type HistoryPatchCompleter = (patch: Partial<AppState>, state: AppState) => Partial<AppState>;

/** A slider or a text field being typed into: its steps merge while the same thing is adjusted. */
const continuousInput = (): boolean => (globalThis as { event?: Event }).event?.type === 'input';

const projectMetadata = (name = 'Sin título'): ProjectMetadata => { const now = new Date().toISOString(); return { id: globalThis.crypto?.randomUUID?.() ?? `project-${Date.now()}`, name, createdAt: now, updatedAt: now }; };

export const createInitialState = (project = projectMetadata(), workspaceLayout = defaultWorkspaceLayout): AppState => ({
  project,
  projectDirty: false,
  model: null,
  sourceModel: null,
  geometryEdits: emptyGeometryEditHistory(),
  geometryEditSession: null,
  layers: [],
  activeLayerId: null,
  genericSelection: null,
  selection: null,
  selectionSet: { layerId: null, entries: [], focus: null },
  activeTool: 'select',
  entityViewLevel: 'suerte',
  selectionLevel: 'suerte',
  viewScope: 'all',
  activeFincaId: null,
  workScope: { fincaIds: [], suerteIds: [], tablonIds: [] },
  remainderVisibility: 'dim',
  labelMode: 'suerte',
  labelConfig: defaultLabelConfig,
  styleConfig: DEFAULT_MASTER_STYLE,
  classificationConfig: defaultClassificationConfig,
  thematicSchemes: [],
  legendConfig: defaultLegendConfig,
  activeWorkspaceModule: 'project',
  savedSelections: [],
  activePresentationPreset: 'free-map',
  presentationWorkspace: defaultPresentationWorkspaceConfig,
  presentationCaptureMode: false,
  floatingMapToolbar: { position: 'default', x: 0, y: 12 },
  workspaceLayout: normalizeWorkspaceLayout(workspaceLayout),
  operationalFields: [],
  excelJoins: [],
  subareas: [],
  scenarios: { active: null, items: [] },
  subareaLabelsVisible: true,
  attributeFilters: [],
  basemap: 'hybrid',
  hybridReference: defaultHybridReferenceVisibility,
  casurHydricVisibility: defaultCasurHydricVisibility,
  casurHydricReference: null,
  casurHydricSelection: null,
  casurHydricLoading: false,
  casurHydricError: null,
  loadingMessage: null,
  error: null,
});

const persistentKeys = new Set<keyof AppState>([
  'project', 'model', 'sourceModel', 'geometryEdits', 'layers', 'activeLayerId', 'selection', 'selectionSet', 'entityViewLevel', 'selectionLevel', 'viewScope', 'activeFincaId', 'workScope', 'remainderVisibility',
  'labelMode', 'labelConfig', 'styleConfig', 'classificationConfig', 'thematicSchemes', 'legendConfig', 'savedSelections', 'activePresentationPreset',
  'presentationWorkspace', 'floatingMapToolbar', 'operationalFields', 'excelJoins', 'subareas', 'scenarios', 'subareaLabelsVisible', 'attributeFilters', 'basemap', 'hybridReference',
  'workspaceLayout', 'casurHydricVisibility',
]);

export class AppStore {
  private state: AppState;
  private readonly listeners = new Set<Listener>();
  private history: EditHistory | null = null;
  private completeHistoryPatch: HistoryPatchCompleter = (patch) => patch;
  /** The step being recorded: opened by a user update, joined by the updates its listeners make. */
  private pending: PendingStep | null = null;
  private applyingHistory = false;

  constructor(initialState: AppState = createInitialState()) { this.state = initialState; }

  getState(): AppState {
    return this.state;
  }

  /** V1.9.0 E5: global Deshacer / Rehacer over the undoable parts of the project (core/history). */
  attachHistory(history: EditHistory, complete?: HistoryPatchCompleter): void {
    this.history = history;
    if (complete) this.completeHistoryPatch = complete;
  }

  update(patch: Partial<AppState>, options: UpdateOptions = {}): void {
    const previous = this.state;
    const persistentChange = Object.keys(patch).some((key) => persistentKeys.has(key as keyof AppState));
    const projectDirty = patch.projectDirty ?? (options.markDirty === false ? this.state.projectDirty : persistentChange ? true : this.state.projectDirty);
    // V1.9.0: the legend lives in the classification; `legendConfig` is only its mirror and always follows it.
    const legendConfig = patch.classificationConfig ? patch.classificationConfig.legend : patch.legendConfig ?? this.state.legendConfig;
    this.state = { ...this.state, ...patch, legendConfig, projectDirty };
    const opens = Boolean(this.history) && !this.applyingHistory && !this.pending && options.history === undefined && options.markDirty !== false && this.changesUndoable(previous);
    if (opens) this.pending = { before: {}, keys: new Set(), continuous: continuousInput() };
    if (this.pending && !this.applyingHistory) this.recordBefore(this.pending, previous);
    try { this.listeners.forEach((listener) => listener(this.state, previous)); }
    finally {
      if (opens && this.pending) { const step = this.pending; this.pending = null; this.history!.commit(step, this.state); }
    }
    if (options.history === 'reset') { this.pending = null; this.history?.clear(); }
  }

  replace(state: AppState): void {
    const previous = this.state;
    this.state = state.legendConfig === state.classificationConfig.legend ? state : { ...state, legendConfig: state.classificationConfig.legend };
    // Another project (opened or new): its own history starts empty.
    this.pending = null; this.history?.clear();
    this.listeners.forEach((listener) => listener(this.state, previous));
  }

  /** Undoes the last step; false when there is none. The patch is completed (derived model) and marks the project dirty. */
  undo(): boolean { return this.applyStep('undo'); }
  redo(): boolean { return this.applyStep('redo'); }

  private applyStep(direction: 'undo' | 'redo'): boolean {
    const history = this.history; if (!history || this.pending || this.applyingHistory) return false;
    const entry: HistoryEntry | null = direction === 'undo' ? history.takeUndo() : history.takeRedo();
    if (!entry) return false;
    const values = (direction === 'undo' ? entry.before : entry.after) as Partial<AppState>;
    const other = (direction === 'undo' ? entry.after : entry.before) as Partial<AppState>;
    this.applyingHistory = true;
    try { this.update(this.completeHistoryPatch(keepViewChoices(values, other, this.state), this.state)); }
    finally { this.applyingHistory = false; }
    if (direction === 'undo') history.undone(entry); else history.redone(entry);
    return true;
  }

  private changesUndoable(previous: AppState): boolean {
    for (const key of UNDOABLE_KEYS) if (this.state[key] !== previous[key]) return true;
    return false;
  }

  private recordBefore(step: PendingStep, previous: AppState): void {
    UNDOABLE_KEYS.forEach((key) => { if (!step.keys.has(key) && this.state[key] !== previous[key]) { step.keys.add(key); (step.before as Record<string, unknown>)[key] = previous[key]; } });
  }

  markSaved(updatedAt = new Date().toISOString()): void {
    this.update({ project: { ...this.state.project, updatedAt }, projectDirty: false }, { markDirty: false });
  }

  markDirty(): void { if (!this.state.projectDirty) this.update({ projectDirty: true }, { markDirty: false }); }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
